const TRUSTED_LOGIN_REDIRECT_ORIGINS = new Set([
  'https://my.careit.com',
  'https://my.careitapp.com',
  'https://staging.careit.com',
  'https://staging.careitapp.com',
  'http://localhost:3979',
  'http://127.0.0.1:3979',
]);

/**
 * Absolute URL to send the user to after login, or null when `value` is
 * missing or points somewhere we don't trust.
 * @param {string} value
 * @returns {string | null}
 */
export function getTrustedLoginRedirect(value) {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    const allowedOrigins = new Set([
      window.location.origin,
      ...TRUSTED_LOGIN_REDIRECT_ORIGINS,
    ]);
    if (!url.username && !url.password && allowedOrigins.has(url.origin)) {
      return url.toString();
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Same as `getTrustedLoginRedirect` but keeps the result relative, for
 * react-router `<Redirect to>` which can't take a cross-origin URL.
 * @param {string} value
 * @returns {string | null}
 */
export function getTrustedLoginRedirectPath(value) {
  const trusted = getTrustedLoginRedirect(value);
  if (!trusted) return null;
  const url = new URL(trusted);
  // Only same-origin destinations can be handed to the router.
  if (url.origin !== window.location.origin) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Login path carrying `location` so the destination survives sign-in.
 * Without this an emailed donation link is lost: the guard sends the user to
 * a bare /auth/login and they land on the dashboard root instead.
 * @param {string} loginPath
 * @param {{pathname: string, search?: string, hash?: string}} location
 * @returns {string}
 */
export function buildLoginPathWithRedirect(loginPath, location) {
  const destination = `${location.pathname}${location.search || ''}${
    location.hash || ''
  }`;
  // Nothing worth preserving about the dashboard root; keep the URL clean.
  if (!destination || destination === '/') return loginPath;
  return `${loginPath}?redirect=${encodeURIComponent(destination)}`;
}

export { TRUSTED_LOGIN_REDIRECT_ORIGINS };
